import { Switch } from "@wags/ui/switch.tsx";
import { useRef, useState } from "react";
import { Header } from "@/components/Header";
import { SettingsBackLink } from "@/components/shared/SettingsBackLink";
import { updateCommunicationPreferences } from "./profile-settings";

export function EmailPreferencesSurface({
    initialMarketingEmails,
    canUpdate,
    showHeader = false,
}: {
    initialMarketingEmails: boolean;
    canUpdate: boolean;
    showHeader?: boolean;
}) {
    const [marketingEmails, setMarketingEmails] = useState(
        initialMarketingEmails,
    );
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const updateInFlight = useRef(false);

    const updateMarketingEmails = async (checked: boolean) => {
        if (updateInFlight.current || !canUpdate) return;
        updateInFlight.current = true;
        const previous = marketingEmails;
        setMarketingEmails(checked);
        setSaving(true);
        setErrorMessage(null);
        try {
            await updateCommunicationPreferences({
                data: { allowMarketingEmails: checked },
            });
        } catch {
            setMarketingEmails(previous);
            setErrorMessage("Could not save that change. Try again.");
        }
        updateInFlight.current = false;
        setSaving(false);
    };

    return (
        <div className="min-h-dvh w-full bg-maltese-white">
            {showHeader && <Header className="hidden md:block" />}

            <main className="mx-auto flex w-full max-w-[1138px] flex-col px-5 pt-8 pb-28 md:px-6 md:pt-[132px] md:pb-24">
                <div className="mb-8 flex flex-col gap-4 md:mb-11">
                    <SettingsBackLink to="/settings" label="Settings" />
                    <h1 className="font-family-heading text-[32px] leading-none tracking-[-0.02em] text-doberman-black md:text-[52px]">
                        Email preferences
                    </h1>
                </div>

                <div className="flex flex-col gap-11 md:gap-12">
                    <section className="flex flex-col">
                        <p className="border-b border-doberman-black/8 pb-3 text-[13px] font-medium leading-4 text-doberman-black/55">
                            What we email you about
                        </p>
                        <div className="flex min-h-[74px] items-center justify-between gap-4 border-b border-doberman-black/8 py-4 md:min-h-[83px]">
                            <div className="min-w-0">
                                <p className="text-[17px] font-medium leading-5 tracking-[-0.01em] text-doberman-black md:text-[18px] md:leading-[22px]">
                                    Marketing emails
                                </p>
                                <p className="mt-1 text-[14px] leading-[18px] text-doberman-black/55 md:hidden">
                                    Tips, updates, and news from Wags.
                                </p>
                                <p className="mt-1 hidden text-[15px] leading-5 text-doberman-black/55 md:block">
                                    Occasional tips, product updates, and news
                                    from Wags.
                                </p>
                            </div>
                            <Switch
                                checked={marketingEmails}
                                disabled={saving || !canUpdate}
                                onCheckedChange={(checked) =>
                                    void updateMarketingEmails(checked)
                                }
                                aria-label="Marketing emails"
                                aria-busy={saving}
                            />
                        </div>
                        {errorMessage && (
                            <output className="pt-3 text-[14px] leading-[18px] text-doberman-black/55">
                                {errorMessage}
                            </output>
                        )}
                        {!canUpdate && (
                            <output className="pt-3 text-[14px] leading-[18px] text-doberman-black/55">
                                Sign in to update your email preferences.
                            </output>
                        )}
                    </section>

                    <section className="flex flex-col">
                        <p className="border-b border-doberman-black/8 pb-3 text-[13px] font-medium leading-4 text-doberman-black/55">
                            Always on
                        </p>
                        <div className="flex min-h-[92px] items-center justify-between gap-4 border-b border-doberman-black/8 py-4 md:min-h-[83px]">
                            <div className="min-w-0">
                                <p className="text-[17px] font-medium leading-5 tracking-[-0.01em] text-doberman-black md:text-[18px] md:leading-[22px]">
                                    Account & contact emails
                                </p>
                                <p className="mt-1 text-[14px] leading-[18px] text-doberman-black/55 md:text-[15px] md:leading-5">
                                    Sign-in, contact confirmations, and
                                    security. These keep your account working.
                                </p>
                            </div>
                            <span className="shrink-0 rounded-label bg-havanese px-3 py-1 text-[13px] font-medium leading-4 text-doberman-black/55">
                                Always on
                            </span>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}
